// ImageSourcePicker.js
// Two-card choice gate shown before Design Studio tab.
// User picks: AI Image Creation OR Manual Design Studio.

import React from 'react';

const PLATFORM_LABELS = {
  instagram: 'Instagram', facebook: 'Facebook', linkedin: 'LinkedIn',
  twitter: 'Twitter/X', tiktok: 'TikTok', email: 'Email', youtube: 'YouTube',
  googleBusiness: 'Google Business',
};
const CONTENT_TYPE_LABELS = {
  educational: 'Educational', promotional: 'Promotional',
  inspirational: 'Inspirational', entertainment: 'Entertainment',
};

export default function ImageSourcePicker({ day, onChooseAI, onChooseStudio }) {
  const platform = PLATFORM_LABELS[day?.platform] || (day?.platform || 'Social');
  const contentType = CONTENT_TYPE_LABELS[day?.contentType] || (day?.contentType || '');

  return (
    <div className="flex-1 flex flex-col items-center justify-center min-h-[520px] bg-gradient-to-b from-slate-50 to-white p-8">

      {/* Header */}
      <div className="text-center mb-10 max-w-lg">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 text-[11px] font-bold rounded-full mb-4 uppercase tracking-wider">
          <i className="fas fa-image text-[9px]" />Day {day?.day} · {platform} · {contentType}
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">How do you want to create your image?</h2>
        <p className="text-sm text-slate-500">
          Choose your image creation workflow. You can always switch later.
        </p>
      </div>

      {/* Two cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl w-full">

        {/* ── Card 1: AI Image Creator ── */}
        <button
          onClick={onChooseAI}
          className="group relative flex flex-col text-left bg-white border-2 border-slate-200 hover:border-violet-400 rounded-2xl p-6 transition-all duration-200 hover:shadow-xl hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-violet-400"
        >
          {/* Recommended badge */}
          <div className="absolute top-4 right-4 px-2 py-0.5 bg-violet-100 text-violet-700 text-[9px] font-bold rounded-full uppercase tracking-wide">
            Recommended
          </div>

          {/* Icon */}
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg group-hover:shadow-purple-200 transition-shadow">
            <i className="fas fa-wand-magic-sparkles text-white text-lg" />
          </div>

          <h3 className="text-lg font-bold text-slate-900 mb-1">AI Image Creator</h3>
          <p className="text-[12px] text-slate-500 mb-5 leading-relaxed">
            AI engineers the perfect prompt from your post context. Generate 3 stunning variations and pick the best one.
          </p>

          {/* Feature list */}
          <ul className="space-y-2 mb-6 flex-1">
            {[
              { icon: 'fa-brain', text: 'AI prompt engineering from your post' },
              { icon: 'fa-rotate', text: 'Regenerate prompt until perfect' },
              { icon: 'fa-layer-group', text: '3 image variations to choose from' },
              { icon: 'fa-palette', text: 'Brand-colour & platform aligned' },
              { icon: 'fa-circle-check', text: 'Send directly to Preview or Studio' },
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px] text-slate-600">
                <i className={`fas ${f.icon} text-violet-500 text-[9px] w-3`} />
                {f.text}
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between">
            <span className="text-[12px] font-bold text-violet-600 group-hover:text-violet-700">
              Create with AI
            </span>
            <div className="w-8 h-8 rounded-full bg-violet-50 group-hover:bg-violet-100 flex items-center justify-center transition-colors">
              <i className="fas fa-arrow-right text-violet-600 text-[10px]" />
            </div>
          </div>
        </button>

        {/* ── Card 2: Manual Design Studio ── */}
        <button
          onClick={onChooseStudio}
          className="group flex flex-col text-left bg-white border-2 border-slate-200 hover:border-slate-700 rounded-2xl p-6 transition-all duration-200 hover:shadow-xl hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-slate-400"
        >
          {/* Icon */}
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center mb-4 shadow-lg group-hover:shadow-slate-200 transition-shadow">
            <i className="fas fa-paint-brush text-white text-lg" />
          </div>

          <h3 className="text-lg font-bold text-slate-900 mb-1">Design Studio</h3>
          <p className="text-[12px] text-slate-500 mb-5 leading-relaxed">
            Full creative control with our Fabric.js canvas. Pick backgrounds, add styled text, upload your logo and build your image.
          </p>

          {/* Feature list */}
          <ul className="space-y-2 mb-6 flex-1">
            {[
              { icon: 'fa-image', text: '12 beautiful background presets' },
              { icon: 'fa-font', text: '14 professional text styles' },
              { icon: 'fa-shapes', text: 'Shapes, lines & overlays' },
              { icon: 'fa-building', text: 'Logo upload with BG removal' },
              { icon: 'fa-sliders', text: 'Full alignment & layer control' },
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px] text-slate-600">
                <i className={`fas ${f.icon} text-slate-500 text-[9px] w-3`} />
                {f.text}
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between">
            <span className="text-[12px] font-bold text-slate-700 group-hover:text-slate-900">
              Open Studio
            </span>
            <div className="w-8 h-8 rounded-full bg-slate-50 group-hover:bg-slate-100 flex items-center justify-center transition-colors">
              <i className="fas fa-arrow-right text-slate-600 text-[10px]" />
            </div>
          </div>
        </button>
      </div>

      {/* Footer note */}
      <p className="text-[11px] text-slate-400 mt-6 text-center">
        <i className="fas fa-info-circle mr-1" />
        AI Image Creator uses your post brief, brand colours and platform to engineer the optimal image prompt.
      </p>
    </div>
  );
}
